import { visit, SKIP } from 'unist-util-visit';

import CoreBase from '../../../../services/engines/CoreBase';
import tabEngine from '../../TabEngine/TabEngine';
import searchQueryEngine from '../../YourSearchTab/Core/SearchQueryEngine';

/** RK pattern — matches dashed UUID v4 (legacy) or dashless 32-char hex (current). Always create a new instance to avoid stateful lastIndex bugs */
const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32}';

/** Prefix used in href to identify RK links — avoids custom protocol stripping by rehype */
const RK_HREF_PREFIX = '#rk-';

/**
 * Returns a fresh regex instance — avoids lastIndex bugs with global flag.
 * @returns {RegExp}
 */
function uuidRegex() {
    return new RegExp(`(${UUID_PATTERN})`, 'gi');
}

/**
 * Handles RK detection and navigation from markdown note bodies.
 * Provides a remark plugin that converts UUID text and inlineCode nodes to link nodes.
 * Singleton — shared across all MarkdownRenderer instances.
 */
class RkLinkifier extends CoreBase {
    _name = 'RkLinkifier';

    /**
     * Returns true if the given href is an RK link.
     * @param {string} href
     * @returns {boolean}
     */
    isRkHref = (href) => {
        return href?.startsWith(RK_HREF_PREFIX) ?? false;
    };

    /**
     * Extracts the RK from an RK href.
     * @param {string} href
     * @returns {string}
     */
    rkFromHref = (href) => {
        return href.replace(RK_HREF_PREFIX, '');
    };

    /**
     * Navigates to a note by RK via the search tab.
     * @param {string} rk - UUID of the target note
     */
    navigateTo = (rk) => {
        try {
            let index = tabEngine.getIndexById('search');

            // Defensive fallback — tabEngine may not be booted yet if this link
            // was clicked from a context outside the normal DashboardPage mount flow.
            if (index === -1) {
                tabEngine.bootup(false);
                index = tabEngine.getIndexById('search');
            }

            if (index === -1) {
                this.logError('navigateTo', 'Search tab not found');
                return;
            }
            tabEngine.setTab(index);
            searchQueryEngine.setQuery(rk);
        } catch (error) {
            this.logError('navigateTo', error.message);
        }
    };

    /**
     * Returns a remark plugin that replaces UUID text and inlineCode nodes with link nodes.
     * Compatible with react-markdown v10+ (mdast AST).
     * @returns {Function} remark plugin
     */
    createRemarkPlugin = () => {
        return () => (tree) => {
            visit(tree, 'text', this._visitTextNode);
            visit(tree, 'inlineCode', this._visitInlineCodeNode);
        };
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Visits a text node — splits on UUIDs and replaces with mixed text/link nodes.
     * Returns SKIP to prevent visit from recursing into the newly inserted nodes.
     * @param {object} node   - mdast text node
     * @param {number} index  - index in parent.children
     * @param {object} parent - parent mdast node
     */
    _visitTextNode = (node, index, parent) => {
        try {
            if (!parent || !uuidRegex().test(node.value)) return;

            const parts = node.value.split(uuidRegex());
            const nodes = parts.map(this._toMdastNode);

            // Replace original text node with the expanded nodes
            parent.children.splice(index, 1, ...nodes);

            // Skip the inserted nodes — prevent infinite recursion
            return [SKIP, index + nodes.length];
        } catch (error) {
            this.logError('_visitTextNode', error.message);
        }
    };

    /**
     * Visits an inlineCode node — if the value is a UUID, replaces it with a link node.
     * Returns SKIP to prevent visit from recursing into the newly inserted node.
     * @param {object} node   - mdast inlineCode node
     * @param {number} index  - index in parent.children
     * @param {object} parent - parent mdast node
     */
    _visitInlineCodeNode = (node, index, parent) => {
        try {
            // Read value before any regex ops
            const rk = node.value;
            if (!parent || !uuidRegex().test(rk)) return;

            parent.children.splice(index, 1, {
                type: 'link',
                url: `${RK_HREF_PREFIX}${rk}`,
                children: [{ type: 'inlineCode', value: rk }],
            });

            // Skip the inserted node — prevent infinite recursion
            return [SKIP, index + 1];
        } catch (error) {
            this.logError('_visitInlineCodeNode', error.message);
        }
    };

    /**
     * Converts a text fragment to an mdast text or link node.
     * @param {string} part - plain text or UUID
     * @returns {object} mdast node
     */
    _toMdastNode = (part) => {
        if (!uuidRegex().test(part)) {
            return { type: 'text', value: part };
        }

        return {
            type: 'link',
            url: `${RK_HREF_PREFIX}${part}`,
            children: [{ type: 'text', value: part }],
        };
    };
}

const rkLinkifier = new RkLinkifier();
export default rkLinkifier;
